import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Play,
  Settings,
  BookOpen,
  Clock,
  Target,
  RotateCcw,
  Loader2,
  Zap,
  Brain,
  Filter,
  AlertTriangle
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

const CustomSession = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [incorrectCount, setIncorrectCount] = useState(0);
  
  // Session configuration
  const [config, setConfig] = useState({
    track: user?.selected_track || 'PMP',
    questionCount: 20,
    difficulty: 'all',
    onlyIncorrect: false,
    timeLimit: 0  // 0 = no limit
  });

  // Track info
  const trackInfo = {
    PMP: { name: 'PMP', fullName: 'Project Management Professional', maxQuestions: 180, color: 'bg-[#0047AB]' },
    CAPM: { name: 'CAPM', fullName: 'Certified Associate in Project Management', maxQuestions: 150, color: 'bg-purple-600' },
    PSM: { name: 'PSM', fullName: 'Professional Scrum Master', maxQuestions: 80, color: 'bg-green-600' },
    PSPO: { name: 'PSPO', fullName: 'Professional Scrum Product Owner', maxQuestions: 80, color: 'bg-purple-700' }
  };

  const currentTrack = trackInfo[config.track] || trackInfo.PMP;

  useEffect(() => {
    if (!user?.is_guest) {
      fetchIncorrectCount();
    }
  }, [config.track]);

  const fetchIncorrectCount = async () => {
    try {
      const response = await fetch(`${API}/exams/incorrect-questions`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setIncorrectCount(data.count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch incorrect questions:', error);
    }
  };

  const startSession = async () => {
    // Block guests from custom session
    if (user?.is_guest) {
      toast.error('Custom Session is only available for registered users. Please register or purchase a course.');
      navigate('/upgrade');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/exams/custom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          track: config.track,
          question_count: config.questionCount,
          difficulty: config.difficulty === 'all' ? null : config.difficulty,
          only_incorrect: config.onlyIncorrect,
          time_limit: config.timeLimit || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to start session');
      }

      const data = await response.json();

      // Store session data and navigate to training
      sessionStorage.setItem('customSession', JSON.stringify({
        attemptId: data.attempt_id,
        questions: data.questions,
        timeLimit: data.time_limit,
        track: config.track,  // Store the selected track
        mode: 'custom'
      }));

      navigate('/training', { state: { customSession: true, attemptId: data.attempt_id } });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const presets = [
    { name: 'Quick', icon: Zap, questions: 10, time: 0, difficulty: 'all', description: '10 questions, no time limit' },
    { name: 'Standard', icon: Target, questions: 20, time: 30, difficulty: 'all', description: '20 questions, 30 minutes' },
    { name: 'Intensive', icon: Brain, questions: 50, time: 60, difficulty: 'hard', description: '50 hard questions, 60 min' },
  ];

  const applyPreset = (preset) => {
    setConfig({
      ...config,
      questionCount: Math.min(preset.questions, currentTrack.maxQuestions),
      timeLimit: preset.time,
      difficulty: preset.difficulty
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <h1 className="font-semibold text-lg flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Custom Session
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Track Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Select Course
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(trackInfo).map(([id, info]) => (
                <button
                  key={id}
                  onClick={() => setConfig({ ...config, track: id, questionCount: Math.min(config.questionCount, info.maxQuestions) })}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    config.track === id
                      ? 'border-[#0047AB] bg-[#0047AB]/5'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                  data-testid={`track-${id}`}
                >
                  <span className={`inline-block w-10 h-10 rounded-lg ${info.color} text-white font-bold text-sm flex items-center justify-center mb-2 mx-auto`}>
                    {info.name}
                  </span>
                  <p className="text-xs text-slate-500">{info.maxQuestions} questions</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Presets */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Quick Select</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              {presets.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-[#0047AB] hover:bg-[#0047AB]/5 transition-all text-left"
                  data-testid={`preset-${preset.name.toLowerCase()}`}
                >
                  <preset.icon className="w-6 h-6 text-[#0047AB] mb-2" />
                  <p className="font-medium text-sm">{preset.name}</p>
                  <p className="text-xs text-slate-500">{preset.description}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Configuration */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Question Count */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Number of Questions</Label>
                <Badge variant="outline" className="font-mono">
                  {config.questionCount}
                </Badge>
              </div>
              <Slider
                value={[config.questionCount]}
                onValueChange={(value) => setConfig({ ...config, questionCount: value[0] })}
                min={5}
                max={currentTrack.maxQuestions}
                step={5}
                className="w-full"
                data-testid="question-count-slider"
              />
              <div className="flex justify-between text-xs text-slate-500">
                <span>5</span>
                <span>{currentTrack.maxQuestions}</span>
              </div>
            </div>

            {/* Difficulty */}
            <div className="space-y-2">
              <Label>Difficulty</Label>
              <Select value={config.difficulty} onValueChange={(v) => setConfig({ ...config, difficulty: v })}>
                <SelectTrigger data-testid="difficulty-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Difficulties</SelectItem>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Time Limit */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Time Limit
                </Label>
                <Badge variant="outline" className="font-mono">
                  {config.timeLimit === 0 ? 'No Limit' : `${config.timeLimit} Min`}
                </Badge>
              </div>
              <Slider
                value={[config.timeLimit]}
                onValueChange={(value) => setConfig({ ...config, timeLimit: value[0] })}
                min={0}
                max={120}
                step={5}
                className="w-full"
                data-testid="time-limit-slider"
              />
              <div className="flex justify-between text-xs text-slate-500">
                <span>No Limit</span>
                <span>120 Min</span>
              </div>
            </div>

            {/* Only Incorrect */}
            {!user?.is_guest && (
              <div className="flex items-center justify-between p-4 bg-slate-100 dark:bg-slate-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <RotateCcw className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="font-medium text-sm">Only Incorrect Questions</p>
                    <p className="text-xs text-slate-500">
                      {incorrectCount > 0 
                        ? `${incorrectCount} questions to review`
                        : 'No incorrect answers yet'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={config.onlyIncorrect}
                  onCheckedChange={(checked) => setConfig({ ...config, onlyIncorrect: checked })}
                  disabled={incorrectCount === 0}
                  data-testid="only-incorrect-switch"
                />
              </div>
            )}

            {user?.is_guest && (
              <div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm text-amber-800 dark:text-amber-200">Guest Mode</p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      As a guest, you can answer a maximum of 50 questions. Register for unlimited access.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary & Start */}
        <Card className={`border-2 ${config.questionCount > 0 ? 'border-[#0047AB]' : 'border-slate-200'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-lg">{currentTrack.fullName}</h3>
                <p className="text-sm text-slate-500">
                  {config.questionCount} questions
                  {config.difficulty !== 'all' && ` • ${config.difficulty === 'easy' ? 'Easy' : config.difficulty === 'medium' ? 'Medium' : 'Hard'}`}
                  {config.timeLimit > 0 && ` • ${config.timeLimit} minutes`}
                  {config.onlyIncorrect && ' • Review only'}
                </p>
              </div>
              <div className={`w-14 h-14 rounded-xl ${currentTrack.color} flex items-center justify-center`}>
                <span className="text-white font-bold">{currentTrack.name}</span>
              </div>
            </div>

            <Button
              onClick={startSession}
              disabled={loading || config.questionCount === 0}
              className="w-full bg-[#0047AB] hover:bg-[#003380] h-12"
              data-testid="start-custom-session-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Play className="w-5 h-5 mr-2" />
              )}
              Start Session
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CustomSession;
